import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap, takeUntil } from 'rxjs';
import {
  ReportContentData,
  ReportContentPayload,
  ReportFile,
  ReportReason,
  ReportSubmitPayload,
} from '../../interfaces/report-content';
import { ReportsService } from './reports.service';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { CommonService } from '../common/common.service';
import { ToastType } from '../../interfaces/common';

@Injectable({
  providedIn: 'root',
})
export class ReportContentService extends DestroyBase {
  private reportService = inject(ReportsService);
  private commonService = inject(CommonService);
  private isOpenSubject = new BehaviorSubject<boolean>(false);
  private modalDataSubject = new BehaviorSubject<ReportContentData | null>(
    null,
  );

  private reasonsSubject = new BehaviorSubject<ReportReason[]>([]);
  private isSubmittingSubject = new BehaviorSubject<boolean>(false);
  private errorMessageSubject = new BehaviorSubject<string | null>(null);

  readonly isOpen$ = this.isOpenSubject.asObservable();
  readonly modalData$ = this.modalDataSubject.asObservable();
  readonly reasons$ = this.reasonsSubject.asObservable();
  readonly isSubmitting$ = this.isSubmittingSubject.asObservable();
  readonly errorMessage$ = this.errorMessageSubject.asObservable();

  open(data: ReportContentData): void {
    this.errorMessageSubject.next(null);
    this.reportService
      .getReportReasons(data.entityType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.reasonsSubject.next(res);
        this.modalDataSubject.next(data);
        this.isOpenSubject.next(true);
      });
  }

  close(): void {
    this.isOpenSubject.next(false);
    this.modalDataSubject.next(null);
    this.reasonsSubject.next([]);
    this.isSubmittingSubject.next(false);
    this.errorMessageSubject.next(null);
  }

  clearError(): void {
    this.errorMessageSubject.next(null);
  }

  getReasons(): ReportReason[] {
    return this.reasonsSubject.value;
  }

  getReportContent(): ReportContentData | null {
    return this.modalDataSubject.value;
  }

  createReport(payload: ReportContentPayload): void {
    this.errorMessageSubject.next(null);

    if (!payload.reason_id) {
      this.errorMessageSubject.next('Report reason is required');
      return;
    }

    const modalData = this.modalDataSubject.value;

    if (!modalData) {
      this.errorMessageSubject.next('Unable to create report');
      return;
    }

    this.isSubmittingSubject.next(true);

    const upload$: Observable<ReportFile[]> = payload.file_ids?.length
      ? this.reportService.uploadReportFiles(
          payload.file_ids,
          modalData.entityId,
          modalData.entityType,
          payload.reason_id,
        )
      : of([]);

    upload$
      .pipe(
        switchMap((files: ReportFile[]) => {
          const reqBody: ReportSubmitPayload = {
            entity_id: modalData.entityId,
            entity_type: modalData.entityType,
            reason_id: payload.reason_id,
          };

          if (payload.severity !== undefined && payload.severity !== null) {
            reqBody.severity = payload.severity;
          }

          if (payload.details?.trim()) {
            reqBody.details = payload.details;
          }

          if (files.length) {
            reqBody.file_ids = files.map((file) => file.file_id);
          }

          return this.reportService.createReport(reqBody);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.isSubmittingSubject.next(false);
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Report created successfully',
          });

          this.close();
        },
        error: (err) => {
          this.isSubmittingSubject.next(false);
          const errorMsg =
            err?.error?.detail ||
            err?.message ||
            'Failed to create report. Please try again.';
          this.errorMessageSubject.next(errorMsg);
        },
      });
  }
}
